import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack,
  IconButton,
  CircularProgress,
  Typography,
  Button,
  Box,
  useTheme
} from '@mui/material'
import { Add, Remove } from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { formatPressure } from '../utils/string'
import { AuroraSetMinPressureLimitActionInput } from '../types/AuroraAwhpActionInput'
import PinCodeDialog from './PinCodeDialog'
import { useDialog } from '../hooks'
import { AuroraAwhpActionNack } from '../errors'
export interface AuroraMinPressureLimitControlProps {
  value: number | null
  loading?: boolean
  onSubmit: (input: AuroraSetMinPressureLimitActionInput) => Promise<void>
}

export default function AuroraMinPressureLimitControl({
  value,
  loading = false,
  onSubmit
}: AuroraMinPressureLimitControlProps) {
  const { t } = useTranslation('aurora')

  const theme = useTheme()

  const { open, openDialog, closeDialog } = useDialog()

  const [minPressLimit, setMinPressLimit] = useState<number | null>(null)

  function handleIncrement() {
    setMinPressLimit((prev) => {
      const current = prev !== null ? prev : value
      if (current !== null) {
        return Math.ceil(current * 10) / 10 + 0.1
      }
      return null
    })
  }

  function handleDecrement() {
    setMinPressLimit((prev) => {
      const current = prev !== null ? prev : value
      if (current !== null) {
        return Math.ceil(current * 10) / 10 - 0.1
      }
      return null
    })
  }

  async function handleSubmit(pin: number) {
    closeDialog()

    if (minPressLimit === null) {
      return
    }

    try {
      await onSubmit({
        alarms: {
          pin,
          minPressLimit
        }
      })

      setMinPressLimit(null)
    } catch (error) {
      if (error instanceof AuroraAwhpActionNack) {
        switch (error.message) {
          case 'PIN_MISSING':
          case 'PIN_REJECTED':
            openDialog()
            break
        }
      }
    }
  }

  function handleReset() {
    setMinPressLimit(null)
  }

  return (
    <Box
      sx={{
        p: 4,
        pb: 6,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        background: theme.palette.background.default
      }}
    >
      <Stack direction="column" alignItems="center" gap={1}>
        <Typography color="text.secondary">{t('minPressureLimit')}</Typography>

        {loading ? (
          <CircularProgress size={24} sx={{ my: 2 }} />
        ) : (
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 1 }}
            gap={2}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={3}>
              <IconButton
                color={minPressLimit !== null ? 'primary' : 'inherit'}
                disabled={loading}
                size="small"
                sx={{ border: '1px solid' }}
                onClick={handleDecrement}
              >
                <Remove />
              </IconButton>

              {minPressLimit !== null ? (
                <Typography variant="h2" color={loading ? 'text.disabled' : 'primary'}>
                  {formatPressure(minPressLimit, 'bar', 1)}
                </Typography>
              ) : (
                <Typography variant="h2" color="text.primary">
                  {formatPressure(value, 'bar', 1)}
                </Typography>
              )}

              <IconButton
                color={minPressLimit !== null ? 'primary' : 'inherit'}
                disabled={loading}
                size="small"
                sx={{
                  border: '1px solid'
                }}
                onClick={handleIncrement}
              >
                <Add />
              </IconButton>
            </Stack>

            {minPressLimit !== null && (
              <Stack direction="row" alignItems="center" gap={2}>
                <LoadingButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  loading={loading}
                  onClick={openDialog}
                >
                  {t('actions:confirm')}
                </LoadingButton>

                {!loading && (
                  <Button variant="text" color="inherit" size="small" onClick={handleReset}>
                    {t('actions:cancel')}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>

      <PinCodeDialog open={open} onClose={closeDialog} onSubmit={handleSubmit} />
    </Box>
  )
}
